import {
  ApiClientError,
  apiClient,
  getSupabase,
  randomUUID,
  sha256HexBlobStreaming,
} from '@dogdisk/shared'
import { captchaBroker } from './captcha-broker'
import { crc32Base64 } from './crc32'
import { precheckVideo, type VideoPreCheckResult } from './video-precheck'

const PATH_B_CHUNK_SIZE = 5 * 1024 * 1024 // 与 server UploadBigRecommendedChunkSize 对齐
// 小文件阈值 = 单分片大小(2026-06-01 owner:小文件 = 5MB 及以下;大于 5MB 才算大文件)。
// 与 server upload.DefaultSmallUploadMaxBytes(= ChunkSize)对齐(后端 /small 接受 ≤5MB)。
// 前端按 file.size 二选一(注意 ≤:恰好 5MB 仍属小文件):
//   - ≤ 单分片 → POST /gapi/user/upload/small(multipart;同步返 file_info_id)
//   - > 单分片 → Path B 直传 B2:init + chunks(5MB)直 PATCH 到 B2 + finalize(202)
//             + status polling 等 worker postprocess 完成
const SMALL_UPLOAD_THRESHOLD_BYTES = PATH_B_CHUNK_SIZE

// MAX_FILE_SIZE_BYTES 业务单文件上限 = 36.8 GiB(2026-05-22 由 5GB 上调)
// 精确值 37683 MiB = 39,513,489,408 bytes。与 server upload.MaxFileSizeBytes 对齐。
// client 端预检拦截:超出直接 throw,不发起 init / 不进 B2 multipart;server 端
// `service.Init` 也会再拒一次(413 + UPLOAD_SIZE_EXCEEDED)做兜底。
const MAX_FILE_SIZE_BYTES = 37683 * 1024 * 1024

export type UploadInitResp = {
  upload_id: string
  tus_endpoint: string
  instant_transfer: boolean
  file_info_id?: string
}

export type UploadBigInitResp = {
  upload_id: string
  path: 'b'
  chunk_size_recommended: number
  instant_transfer: boolean
  file_info_id?: string
  // resumed=true:命中一个进行中的既有 session(后端按 user+sha256 二级索引找回)。
  // 前端应 HEAD 查已传 part 数,从断点续传,而非从 part 1 重传。
  resumed?: boolean
  // processing=true:该内容已 100% 传完、后端 postprocess 进行中(session=finalizing/
  // merging/processing)。前端应直接轮询 /status 等 done,而非重传或续传(没有 part 要续)。
  // 典型场景:用户在 finalizing 期间刷新 + 重选同一文件 → 不再从头重传。
  processing?: boolean
}

export type UploadBigFinalizeResp = {
  status: 'processing'
  upload_id: string
  status_url: string
}

export type UploadStatusResp = {
  upload_id: string
  status: 'uploading' | 'finalizing' | 'processing' | 'done' | 'failed' | 'unknown'
  file_info_id?: string
}

export type UploadFinalizeResp = {
  file_info_id: string
  base_file_id: string
  status: string
}

export type UploadSmallResp = {
  file_info_id: string
  base_file_id: string
  sha256: string
  size: number
  status: string
}

export type UploadProgress = {
  bytesUploaded: number
  bytesTotal: number
  percent: number
}

export type UploadOptions = {
  parentId?: string | undefined
  onProgress?: ((p: UploadProgress) => void) | undefined
  onSuccess?: ((resp: UploadFinalizeResp) => void) | undefined
  onError?: ((err: Error) => void) | undefined
  // 2026-06-23:上传子阶段回调。preparing=算 hash 期、transferring=传字节期、
  // finalizing=大文件传完等后端 postprocess 期。UI 据此切「正在准备/完成上传」文案。
  onPhase?: ((phase: 'preparing' | 'transferring' | 'finalizing') => void) | undefined
  // 2026-06-24:preparing 阶段(算整文件 sha256)的进度,0–100。
  // 大文件分片流式哈希时每分片回报一次,驱动「正在准备上传: xx%」。
  onHashProgress?: ((pct: number) => void) | undefined
  // 2026-06-24:同会话暂停/恢复复用首次算出的整文件 sha256。
  //   - precomputedSha:调用方(upload-run)透传上轮 sha → uploadLarge 跳过哈希直走 init,
  //     消除 resume 重算整个文件 hash 的卡顿 + /big/init 延迟。
  //   - onShaComputed:首次现算出 sha 后回报给调用方缓存,供后续 resume 透传。
  precomputedSha?: string | undefined
  onShaComputed?: ((sha: string) => void) | undefined
  // 2026-05-28 plan §6:超免验额度时,后端返 428 CAPTCHA_REQUIRED。
  // 调用方可注入自定义 onCaptchaRequired(返一次性 token);未传则回落到
  // 全局 captchaBroker —— main.tsx 挂 <GlobalCaptchaGate /> 统一弹 modal。
  // 返空字符串视为用户放弃 → uploader 抛 UploadError。
  onCaptchaRequired?: (() => Promise<string>) | undefined
  // 2026-06-07 V1.2 暂停:调用方可注入 AbortSignal。abort 后,大文件分片循环在下一片前停止,
  // 在传 fetch 立即中断。uploader 抛 UploadAbortError(调用方据 signal.aborted 判别暂停 vs 失败)。
  signal?: AbortSignal | undefined
}

// UploadAbortError 表示上传被用户暂停(AbortSignal abort),非真正失败。
export class UploadAbortError extends Error {
  constructor(message = 'upload paused') {
    super(message)
    this.name = 'UploadAbortError'
  }
}

// 分片网络重试(2026-06-24 owner):单片仅在网络错/5xx/超时重试至多 10 次,指数退避+抖动。
// 4xx(除 401,已由 authedFetch 刷 token)立即失败;abort 立即停不重试。
export const CHUNK_MAX_ATTEMPTS = 10

export class NonRetryableUploadError extends Error {}

function chunkBackoffMs(failedAttempt: number): number {
  const base = Math.min(500 * 2 ** (failedAttempt - 1), 15_000)
  return base + base * 0.2 * Math.random()
}

// uploadPartWithRetry — 抽成纯函数便于单测:fetcher 发一次请求,sleep 注入便于 mock。
export async function uploadPartWithRetry(
  fetcher: () => Promise<Response>,
  partNum: number,
  signal: AbortSignal | undefined,
  sleep: (ms: number) => Promise<void> = (ms) => new Promise((r) => setTimeout(r, ms)),
): Promise<Response> {
  let lastErr: Error = new Error(`upload part ${partNum} failed`)
  for (let attempt = 1; attempt <= CHUNK_MAX_ATTEMPTS; attempt++) {
    if (signal?.aborted) throw new UploadAbortError()
    try {
      const res = await fetcher()
      if (res.ok) return res
      const text = await res.text().catch(() => '')
      const e = new Error(`upload part ${partNum} failed: HTTP ${res.status} ${text}`)
      if (res.status < 500) throw new NonRetryableUploadError(e.message) // 4xx 立即失败
      lastErr = e // 5xx 可重试
    } catch (err) {
      if (signal?.aborted) throw new UploadAbortError()
      if (err instanceof NonRetryableUploadError) throw err
      lastErr = err instanceof Error ? err : new Error(String(err)) // 网络错 → 可重试
    }
    if (attempt < CHUNK_MAX_ATTEMPTS) await sleep(chunkBackoffMs(attempt))
  }
  throw lastErr
}

// resolveCaptchaToken 决定如何请求 token:
//   1. 调用方注入 onCaptchaRequired → 用它(老接口,upload.tsx 可保留自己的队列)
//   2. 否则回落到全局 broker(默认所有 uploader 行为)
function resolveCaptchaToken(opts: UploadOptions): Promise<string> {
  if (opts.onCaptchaRequired) return opts.onCaptchaRequired()
  return captchaBroker.request()
}

// UploadErrCode 是后端 errcode(响应 body.code);用于客户端按码路由处理。
// (UPLOAD_QUOTA_EXCEEDED 直接由 UI 层比对字符串,无需常量;若日后需要可加)
const ERR_CAPTCHA_REQUIRED = 'CAPTCHA_REQUIRED'
const ERR_CAPTCHA_INVALID = 'CAPTCHA_INVALID'

// captchaRetryCap 最多重试 3 次(防 token 过期循环 → 卡死)
const CAPTCHA_RETRY_CAP = 3

// parseErrResp 把后端错误响应(JSON: {code, message})解析为 {code, message},
// 失败时退回 {code: '', message: text}。
function parseErrResp(text: string): { code: string; message: string } {
  try {
    const obj = JSON.parse(text) as { code?: unknown; message?: unknown }
    return {
      code: typeof obj.code === 'string' ? obj.code : '',
      message: typeof obj.message === 'string' ? obj.message : text,
    }
  } catch {
    return { code: '', message: text }
  }
}

// UploadError 携带后端 errcode 以便 UI 层分支(toast / 终态)
export class UploadError extends Error {
  code: string
  constructor(code: string, message: string) {
    super(message)
    this.code = code
    this.name = 'UploadError'
  }
}

// abortUpload 统一收尾被暂停的上传:报 onError(UploadAbortError)后抛出。
// 调用方(upload-run)据 signal.aborted 判定为暂停,不置 error 终态。
function abortUpload(opts: UploadOptions): never {
  const e = new UploadAbortError()
  opts.onError?.(e)
  throw e
}

export async function uploadFile(file: File, opts: UploadOptions = {}): Promise<void> {
  if (file.size > MAX_FILE_SIZE_BYTES) {
    const err = new Error(
      `file size ${file.size} bytes exceeds maximum ${MAX_FILE_SIZE_BYTES} bytes (36.8 GiB)`,
    )
    opts.onError?.(err)
    throw err
  }

  // 视频:上传前用 mp4box 预校验编码白名单(§3.4)。不符合直接拒绝,不发起上传。
  // file.type 为空(部分浏览器)时跳过前端预检,交由后端 ffprobe 兜底校验。
  let videoPreCheck: VideoPreCheckResult | undefined
  if (file.type.startsWith('video/')) {
    try {
      videoPreCheck = await precheckVideo(file)
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err))
      opts.onError?.(e)
      throw e
    }
  }

  // 小文件 = 5MB 及以下(≤ 阈值);大于 5MB 才走 Path B 大文件直传。
  if (file.size <= SMALL_UPLOAD_THRESHOLD_BYTES) {
    return uploadSmall(file, opts)
  }
  return uploadLarge(file, opts, videoPreCheck)
}

// SmallCheckResp 是 /gapi/user/upload/small/check 的响应
type SmallCheckResp = {
  instant_transfer: boolean
  file_info_id?: string
  base_file_id?: string
}

// uploadSmall: Path A(≤10MB)两步流程(2026-05-29):
//  1. 前端算整文件 sha256 → POST /small/check 预检秒传;命中则不传 body 直接完成
//  2. 未命中 → multipart POST /small 传 body(带 sha256 供后端复算比对校验)
async function uploadSmall(file: File, opts: UploadOptions): Promise<void> {
  // apiClient 默认走 JSON body;multipart 需绕开,直接 fetch + Bearer。
  const sb = getSupabase()
  const { data: session } = await sb.auth.getSession()
  const token = session.session?.access_token ?? ''
  const authHeader: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {}

  opts.onProgress?.({ bytesUploaded: 0, bytesTotal: file.size, percent: 0 })
  opts.onPhase?.('preparing')

  // 整文件 sha256(前端预算:① 秒传预检 ② 后端复算比对校验)。
  // 分片流式哈希:绕开 >2GB 单 ArrayBuffer 上限 + 产出准备进度。
  const sha = await sha256HexBlobStreaming(file, (f) =>
    opts.onHashProgress?.(Math.round(f * 100)),
  )

  // 构造一次 multipart body(captcha 重试时复用 file 引用,FormData 每次重建)
  const buildForm = (capToken: string | null): FormData => {
    const form = new FormData()
    form.append('file', file, file.name)
    form.append('filename', file.name)
    form.append('sha256', sha) // 后端复算比对(设计 §3.3 step3)
    if (file.type) form.append('mime_type', file.type)
    if (opts.parentId) form.append('parent_id', opts.parentId)
    if (capToken) form.append('captcha_token', capToken)
    return form
  }

  try {
    opts.onPhase?.('transferring')
    // 1. 两步秒传预检:命中 → 不传 body 直接完成
    let capToken: string | null = null
    for (let attempt = 0; attempt <= CAPTCHA_RETRY_CAP; attempt++) {
      const res = await fetch('/gapi/user/upload/small/check', {
        method: 'POST',
        headers: {
          ...authHeader,
          'Content-Type': 'application/json',
          ...(capToken ? { 'X-Captcha-Token': capToken } : {}),
        },
        credentials: 'include',
        signal: opts.signal ?? null,
        body: JSON.stringify({
          sha256: sha,
          filename: file.name,
          mime_type: file.type || undefined,
          parent_id: opts.parentId,
        }),
      })
      const text = await res.text()
      if (res.ok) {
        const data = JSON.parse(text) as SmallCheckResp
        if (data.instant_transfer) {
          opts.onProgress?.({ bytesUploaded: file.size, bytesTotal: file.size, percent: 100 })
          opts.onSuccess?.({
            file_info_id: data.file_info_id ?? '',
            base_file_id: data.base_file_id ?? '',
            status: 'instant',
          })
          return
        }
        break // 未命中 → 走 multipart 上传
      }
      const { code, message } = parseErrResp(text)
      if ((code === ERR_CAPTCHA_REQUIRED || code === ERR_CAPTCHA_INVALID) && attempt < CAPTCHA_RETRY_CAP) {
        // 秒传命中且在 captcha 区:弹 captcha → 重试预检
        capToken = await resolveCaptchaToken(opts)
        if (!capToken) {
          throw new UploadError(code, message || '取消验证码')
        }
        continue
      }
      throw new UploadError(code, message || res.statusText)
    }

    // 2. 未命中 → multipart 上传 body
    capToken = null
    for (let attempt = 0; attempt <= CAPTCHA_RETRY_CAP; attempt++) {
      const res = await fetch('/gapi/user/upload/small', {
        method: 'POST',
        headers: authHeader,
        credentials: 'include',
        signal: opts.signal ?? null,
        body: buildForm(capToken),
      })
      const text = await res.text()
      if (res.ok) {
        const resp = JSON.parse(text) as UploadSmallResp
        opts.onProgress?.({ bytesUploaded: file.size, bytesTotal: file.size, percent: 100 })
        opts.onSuccess?.({
          file_info_id: resp.file_info_id,
          base_file_id: resp.base_file_id,
          status: resp.status,
        })
        return
      }
      const { code, message } = parseErrResp(text)
      if ((code === ERR_CAPTCHA_REQUIRED || code === ERR_CAPTCHA_INVALID) && attempt < CAPTCHA_RETRY_CAP) {
        // 弹 captcha → 拿 token → 重发同一文件
        capToken = await resolveCaptchaToken(opts)
        if (!capToken) {
          throw new UploadError(code, message || '取消验证码')
        }
        continue
      }
      throw new UploadError(code, message || res.statusText)
    }
    throw new UploadError(ERR_CAPTCHA_INVALID, '验证码多次失败,放弃上传')
  } catch (err) {
    // 暂停(abort)→ AbortError;统一收尾为 UploadAbortError(非失败)。
    if (opts.signal?.aborted) abortUpload(opts)
    const e = err instanceof UploadError ? err : err instanceof Error ? err : new Error(String(err))
    opts.onError?.(e)
    throw e
  }
}

// uploadLarge: 走 Path B 直传 B2 multipart(2026-05-20 I.6b)。
//
// 流程:
//  1. POST /gapi/user/upload/big/init       —— 创建 session + B2 multipart Init,返 upload_id
//  2. 串行 PATCH 每个 5MB chunk       —— Upload-Part-Number + Upload-Checksum(sha256)+ 直传 B2
//  3. POST /gapi/user/upload/big/{id}/finalize → 202 + status_url
//  4. 轮询 GET /gapi/user/upload/{id}/status 直到 status="done" 或 "failed"
//
// 与旧 TUS legacy 路径区别:
//   - chunks 不在 server 缓冲(B2 multipart UploadPart),server 内存常量
//   - finalize 异步(asynq worker 跑 postprocess);UI 显示"处理中"
async function uploadLarge(
  file: File,
  opts: UploadOptions,
  videoPreCheck?: VideoPreCheckResult,
): Promise<void> {
  if (opts.signal?.aborted) abortUpload(opts)
  opts.onPhase?.('preparing')
  // 续传复用:调用方透传上轮算出的 sha → 跳过整文件流式哈希(大文件可省数十秒,
  // 且让 /big/init 立即发出,不再卡在哈希后)。首次上传现算并回报供后续复用。
  let sha: string
  if (opts.precomputedSha) {
    sha = opts.precomputedSha
    opts.onHashProgress?.(100)
  } else {
    sha = await sha256HexBlobStreaming(file, (f) => opts.onHashProgress?.(Math.round(f * 100)))
    opts.onShaComputed?.(sha)
  }
  const initBody = {
    filename: file.name,
    size_original: file.size,
    mime_type: file.type || 'application/octet-stream',
    sha256_original: sha,
    parent_id: opts.parentId,
    idempotency_key: randomUUID(),
    // §3.4 后端二次校验(防绕过);仅视频且前端预检通过时带上。
    // ⚠️ JSON key 必须是 `video_pre_check`(后端 upload_big_handlers.go:88 的 tag)。
    video_pre_check: videoPreCheck,
  }

  // init 步骤可能返 428 CAPTCHA_REQUIRED;按 §6 retry-with-token 流程
  let init: UploadBigInitResp
  let capToken: string | null = null
  for (let attempt = 0; ; attempt++) {
    try {
      const headers: Record<string, string> = capToken ? { 'X-Captcha-Token': capToken } : {}
      init = await apiClient.post<UploadBigInitResp>('/gapi/user/upload/big/init', initBody, { headers })
      break
    } catch (err) {
      if (err instanceof ApiClientError &&
          (err.code === ERR_CAPTCHA_REQUIRED || err.code === ERR_CAPTCHA_INVALID) &&
          attempt < CAPTCHA_RETRY_CAP) {
        capToken = await resolveCaptchaToken(opts)
        if (!capToken) {
          const e = new UploadError(err.code, err.message || '取消验证码')
          opts.onError?.(e)
          throw e
        }
        continue
      }
      if (err instanceof ApiClientError) {
        const e = new UploadError(err.code, err.message)
        opts.onError?.(e)
        throw e
      }
      const e = err instanceof Error ? err : new Error(String(err))
      opts.onError?.(e)
      throw e
    }
  }

  if (init.instant_transfer) {
    opts.onProgress?.({ bytesUploaded: file.size, bytesTotal: file.size, percent: 100 })
    opts.onSuccess?.({
      file_info_id: init.file_info_id ?? '',
      base_file_id: '',
      status: 'instant',
    })
    return
  }

  // 已传完、postprocess 进行中:不重传也不续传,直接轮询 /status 等 done。
  // (用户在 finalizing 期间刷新 + 重选同一文件 → 后端按 sha 找回该 session 返 processing。)
  if (init.processing) {
    opts.onPhase?.('finalizing')
    opts.onProgress?.({ bytesUploaded: file.size, bytesTotal: file.size, percent: 100 })
    const statusURL = `/gapi/user/upload/${init.upload_id}/status`
    const fileInfo = await pollUploadStatus(statusURL, opts.signal)
    opts.onSuccess?.({
      file_info_id: fileInfo.file_info_id ?? '',
      base_file_id: '',
      status: 'done',
    })
    return
  }

  opts.onPhase?.('transferring')

  // 串行 PATCH chunks(并行可后续优化)。chunkSize 由 server 推荐,默认 5MB。
  // ⚠️ 续传依赖 chunkSize 与原 session 一致 → part 边界对齐;server 始终推荐同一常量。
  const chunkSize = init.chunk_size_recommended || PATH_B_CHUNK_SIZE
  const partsCount = Math.max(1, Math.ceil(file.size / chunkSize))
  let bytesUploaded = 0

  // 断点续传:命中进行中的 session → HEAD 查已传 part 数,从下一个 part 继续(串行上传保证
  // 已传 parts 连续 1..k)。HEAD 失败(session 过期等)则降级为从头传。
  let startPart = 1
  if (init.resumed) {
    try {
      const head = await authedFetch(`/gapi/user/upload/big/${init.upload_id}`, {
        method: 'HEAD',
        credentials: 'include',
        signal: opts.signal ?? null,
      })
      if (head.ok) {
        const done = parseInt(head.headers.get('Upload-Parts-Count') || '0', 10)
        if (Number.isFinite(done) && done > 0 && done < partsCount) {
          startPart = done + 1
          bytesUploaded = done * chunkSize
          opts.onProgress?.({
            bytesUploaded,
            bytesTotal: file.size,
            percent: (bytesUploaded / file.size) * 100,
          })
        }
      }
    } catch {
      // HEAD 失败 → 保守从头传(startPart=1),不阻断上传。
    }
  }

  for (let partNum = startPart; partNum <= partsCount; partNum++) {
    // 暂停:在下一分片前停止(已传分片保留,resume 时后端 bysha 从断点续)。
    if (opts.signal?.aborted) abortUpload(opts)
    const start = (partNum - 1) * chunkSize
    const end = Math.min(start + chunkSize, file.size)
    const blob = file.slice(start, end)
    const buf = await blob.arrayBuffer()
    const partCrc = await crc32Base64(new Uint8Array(buf))

    // uploadPartWithRetry 只在 res.ok 时 return,否则 throw(网络重试耗尽 / 4xx)→
    // 走到此处即已成功,无需再读 res,直接累计进度。
    try {
      await uploadPartWithRetry(
        () =>
          authedFetch(`/gapi/user/upload/big/${init.upload_id}`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/octet-stream',
              'Content-Length': String(buf.byteLength),
              'Upload-Part-Number': String(partNum),
              'Upload-Checksum': `crc32=${partCrc}`,
            },
            credentials: 'include',
            signal: opts.signal ?? null,
            body: buf,
          }),
        partNum,
        opts.signal,
      )
    } catch (err) {
      // abort → 暂停(不算失败,不 onError);其余(网络重试耗尽/4xx)→ 上报失败。
      if (err instanceof UploadAbortError || opts.signal?.aborted) abortUpload(opts)
      const e = err instanceof Error ? err : new Error(String(err))
      opts.onError?.(e)
      throw e
    }
    bytesUploaded += buf.byteLength
    opts.onProgress?.({
      bytesUploaded,
      bytesTotal: file.size,
      percent: (bytesUploaded / file.size) * 100,
    })
  }

  // 所有 part 已传完,进入「正在完成上传」阶段(finalize + 轮询 postprocess)
  opts.onPhase?.('finalizing')

  // Finalize → 202 + status_url
  if (opts.signal?.aborted) abortUpload(opts)
  const finRes = await authedFetch(`/gapi/user/upload/big/${init.upload_id}/finalize`, {
    method: 'POST',
    credentials: 'include',
    signal: opts.signal ?? null,
  })
  if (!finRes.ok) {
    const text = await finRes.text()
    const e = new Error(`finalize failed: HTTP ${finRes.status} ${text}`)
    opts.onError?.(e)
    throw e
  }
  const finJson = (await finRes.json()) as UploadBigFinalizeResp

  // 轮询 status 直到 done / failed(进度 UI 显示"处理中");abort → 暂停(非失败)。
  let fileInfo: UploadStatusResp
  try {
    fileInfo = await pollUploadStatus(finJson.status_url, opts.signal)
  } catch (err) {
    if (opts.signal?.aborted) abortUpload(opts)
    const e = err instanceof Error ? err : new Error(String(err))
    opts.onError?.(e)
    throw e
  }
  opts.onSuccess?.({
    file_info_id: fileInfo.file_info_id ?? '',
    base_file_id: '',
    status: 'done',
  })
}

// bearerHeader 取「当前」session 的 Bearer 头。每次现取(而非捕获一次复用)→
// 自动吃到前台 autoRefresh 定时器刷新后的新 token。
async function bearerHeader(): Promise<Record<string, string>> {
  const { data } = await getSupabase().auth.getSession()
  const t = data.session?.access_token ?? ''
  return t ? { Authorization: `Bearer ${t}` } : {}
}

// refreshBearerHeader 用 refresh_token 强制走网络刷新一次,返回新 Bearer 头。
// 兜底场景:移动端 Safari 后台/锁屏冻结 JS 定时器 → supabase autoRefresh 没跑 →
// access_token 已过期,getSession() 仍返回 localStorage 里那份死 token。
async function refreshBearerHeader(): Promise<Record<string, string>> {
  const { data } = await getSupabase().auth.refreshSession()
  const t = data.session?.access_token ?? ''
  return t ? { Authorization: `Bearer ${t}` } : {}
}

// authedFetch 现取 Bearer 头发请求;遇 401 强制 refreshSession() 刷新一次后用新 token 重试一次。
// 长上传(分片 PATCH 循环 / HEAD 续传探测 / finalize)耗时可超过 access_token 1h 寿命,
// 尤其移动端后台冻结 autoRefresh 时 token 会在传输途中过期 → 这里就地自愈,不让整条上传 401 失败。
// 调用方在 init.headers 里只放业务头(Content-Type / Upload-* 等),Authorization 由本函数注入。
async function authedFetch(url: string, init: RequestInit): Promise<Response> {
  const base = (init.headers as Record<string, string> | undefined) ?? {}
  const res = await fetch(url, { ...init, headers: { ...base, ...(await bearerHeader()) } })
  if (res.status !== 401) return res
  return fetch(url, { ...init, headers: { ...base, ...(await refreshBearerHeader()) } })
}

// pollUploadStatus — 轮询 GET /gapi/user/upload/:id/status 直到 done / failed。
//
// 间隔 1s,最多 5 min(300 次);超时返 error。worker postprocess 平均 < 60s。
//
// token 现取现用:长上传(尤其移动端)轮询期可能跨过 access_token 过期边界。收到 401 时
// 强制 refreshSession() 刷新一次再重试,避免拿着死 token 空转到超时(文件其实已上传成功)。
export async function pollUploadStatus(
  statusURL: string,
  signal?: AbortSignal,
): Promise<UploadStatusResp> {
  let refreshed = false
  for (let i = 0; i < 300; i++) {
    if (signal?.aborted) throw new UploadAbortError()
    await new Promise((r) => setTimeout(r, 1_000))
    if (signal?.aborted) throw new UploadAbortError()
    const headers = await bearerHeader()
    const res = await fetch(statusURL, { headers, credentials: 'include', signal: signal ?? null })
    if (res.status === 401 && !refreshed) {
      // token 过期(典型:移动端后台冻结 autoRefresh)→ 强制刷新一次,下一轮用新 token 重试。
      await refreshBearerHeader()
      refreshed = true
      continue
    }
    if (res.ok) refreshed = false // 成功后复位:容许后续(罕见)再次过期时再刷新一次
    if (!res.ok) continue // 偶尔 404(session 落库延迟)/ 已刷新后的 401 → 重试
    const data = (await res.json()) as UploadStatusResp
    if (data.status === 'done') return data
    if (data.status === 'failed') throw new Error('upload postprocess failed (see server log)')
    // uploading / finalizing / processing / unknown — 继续轮询
  }
  throw new Error('upload status polling timeout (5min)')
}
